import '../../org-admin/styles/Attendance.css';

// What a day actually looked like: every stretch worked, every gap, and what
// each gap was for.
//
// The month table can only ever say "3 sessions". This is the view that answers
// the real question — worked 10:00–12:10, lunch half an hour, back at the desk,
// out on office work for two hours, finished at six — and it is the only place
// the difference between a lunch break and a client visit is legible.

const HHMM = (d) =>
  d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

const dur = (mins) => {
  const m = Math.max(0, Math.round(mins));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
};

const minutesBetween = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000));

// The gap that follows a session, described by why that session ended. Only
// official work is paid, which is the whole reason the reason is recorded.
//
// ⚠️ `done` is in here too, and it matters. Someone who clocks out "done for
// the day" and then clocks back in later has left a real gap — it counts
// toward the day's break total either way. Drawing only the NAMED gaps made
// the timeline disagree with its own footer: a day showing "11m break" listed
// a single 1m lunch and hid the other ten minutes entirely. Every gap between
// two sessions is drawn; only the reason differs.
const GAP = {
  break: { label: 'Break', counted: false },
  lunch: { label: 'Lunch', counted: false },
  official_work: { label: 'Out on office work', counted: true },
  done: { label: 'Away', counted: false },
};

// An unrecognised reason still leaves a gap in the day, so it gets drawn as a
// plain one rather than vanishing.
const UNKNOWN_GAP = { label: 'Away', counted: false };

/**
 * Turn the session list into an alternating run of worked stretches and gaps.
 *
 * Built here rather than on the server because it is purely presentational —
 * the totals the register reports are derived in the model's pre-save hook, and
 * a second computation that could disagree with them is exactly what this
 * codebase avoids elsewhere.
 */
function buildSegments(sessions) {
  const rows = [...(sessions || [])]
    .filter((s) => s.inAt)
    .sort((a, b) => new Date(a.inAt) - new Date(b.inAt));

  const segments = [];
  rows.forEach((s, i) => {
    segments.push({
      kind: 'work',
      from: s.inAt,
      to: s.outAt,
      minutes: s.outAt ? minutesBetween(s.inAt, s.outAt) : null,
      workMode: s.workMode || 'remote',
      officeName: s.officeName || null,
      source: s.source || 'web',
      note: s.note || '',
      open: !s.outAt,
    });

    const next = rows[i + 1];
    // A gap only exists BETWEEN two sessions — after the last one, or while one
    // is still open, there is nothing to describe.
    if (!s.outAt || !next) return;
    const meta = GAP[s.endReason] || UNKNOWN_GAP;
    // A zero-length gap (clocked straight back in) is noise, not information.
    const minutes = minutesBetween(s.outAt, next.inAt);
    if (minutes === 0) return;
    segments.push({
      kind: 'gap',
      from: s.outAt,
      to: next.inAt,
      minutes,
      label: meta.label,
      counted: meta.counted,
    });
  });
  return segments;
}

function DayTimeline({ day, compact = false }) {
  const segments = buildSegments(day?.sessions);

  if (!segments.length) {
    return <p className="att-tl-empty">Nothing recorded for this day.</p>;
  }

  return (
    <div className={`att-tl${compact ? ' att-tl-compact' : ''}`}>
      <ol className="att-tl-list">
        {segments.map((seg, i) => (
          <li
            key={`${seg.kind}-${i}`}
            className={`att-tl-item is-${seg.kind}${seg.kind === 'gap' && seg.counted ? ' is-counted' : ''}`}
          >
            <span className="att-tl-marker" aria-hidden="true" />

            <span className="att-tl-time">
              {HHMM(seg.from)}
              <span className="att-tl-dash">–</span>
              {seg.open ? <em>now</em> : HHMM(seg.to)}
            </span>

            <span className="att-tl-body">
              {seg.kind === 'work' ? (
                <>
                  <span className="att-tl-label">
                    {seg.open ? 'Working' : 'Worked'}
                    {seg.source === 'manual' && (
                      // Hand-entered hours are flagged wherever they appear —
                      // trusting people to fill their own day is not the same
                      // as hiding that they did.
                      <span className="att-tl-tag is-manual">entered by hand</span>
                    )}
                    {seg.source === 'auto' && (
                      // The one out-time nobody asserted: the day-end job closed
                      // this. Flagged here because this timeline is where the
                      // person who stayed late finds out their clock stopped
                      // without them, and adds the rest by hand.
                      <span className="att-tl-tag is-auto">clocked out automatically</span>
                    )}
                  </span>
                  <span className={`att-tl-mode is-${seg.workMode}`}>
                    {seg.workMode === 'office' ? seg.officeName || 'Office' : 'Remote'}
                  </span>
                </>
              ) : (
                <span className="att-tl-label">
                  {seg.label}
                  <span className={`att-tl-tag${seg.counted ? ' is-counted' : ''}`}>
                    {seg.counted ? 'counted as work' : 'not counted'}
                  </span>
                </span>
              )}
              {seg.note && <span className="att-tl-note">{seg.note}</span>}
            </span>

            <span className="att-tl-dur">{seg.minutes == null ? '—' : dur(seg.minutes)}</span>
          </li>
        ))}
      </ol>

      <div className="att-tl-totals">
        <span><b>{dur(day.workedMinutes || 0)}</b> worked</span>
        {day.officialWorkMinutes > 0 && (
          // Stated as "of which" rather than as a separate figure, because it
          // is a share of the worked total and not an addition to it.
          <span className="att-muted">of which {dur(day.officialWorkMinutes)} out on office work</span>
        )}
        {day.breakMinutes > 0 && <span className="att-muted">{dur(day.breakMinutes)} break</span>}
        {day.overtimeMinutes > 0 && <span className="att-tl-over">+{dur(day.overtimeMinutes)} overtime</span>}
        {day.shortfallMinutes > 0 && <span className="att-tl-short">{dur(day.shortfallMinutes)} short</span>}
      </div>
    </div>
  );
}

export default DayTimeline;
export { buildSegments };
